import { NavLink } from 'react-router-dom';
import { cn } from '@/components/ui';
import { useQuickNav, type NavItem } from './useQuickNav';
import { useT } from '@/core/i18n';

/* =============================================================================
 * The modules somebody actually uses, one thumb away
 * -----------------------------------------------------------------------------
 * On a phone the admin app has a sidebar behind a hamburger: two taps and a
 * scan of eleven entries to reach the one screen somebody opens forty times a
 * day. The employee portal has had a bottom bar all along, and it is plainly
 * better for the way a phone is held.
 *
 * -----------------------------------------------------------------------------
 * Chosen rather than guessed
 * -----------------------------------------------------------------------------
 * There is no correct default five. A storekeeper lives in Documents and never
 * opens Payroll; an accountant is the reverse; a fleet supervisor wants Fleet
 * and Attendance and nothing else. Picking for them would be right for one
 * person and wrong for the rest, so the bar is theirs to set — and until they
 * do, it holds the first few they have permission for, which is at least not a
 * guess dressed as a recommendation.
 *
 * Kept on the device, like light and dark and the personal colour. It is a
 * preference about this phone, it does not need to follow anybody between
 * devices, and storing it server-side would turn a convenience into a record
 * about how somebody works.
 * ========================================================================== */

export function QuickNav({ items }: { items: NavItem[] }) {
  const t = useT();
  const { chosen } = useQuickNav(items);

  // Nothing to show is not a bar. One entry is a bar with one thing on it,
  // which is worse than the hamburger it replaces.
  if (chosen.length < 2) return null;

  return (
    <nav
      className={cn(
        'fixed bottom-0 inset-x-0 z-30 lg:hidden',
        'bg-surface border-t border-line pb-safe',
        'shadow-[0_-1px_12px_-4px_rgba(0,0,0,0.25)]',
      )}
    >
      <div
        className="grid"
        /* Sized from the count rather than a fixed grid-cols-5: somebody who
           picks four should get four wide targets, not four and a gap. */
        style={{ gridTemplateColumns: `repeat(${chosen.length}, minmax(0, 1fr))` }}
      >
        {chosen.map(({ to, labelKey, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            viewTransition
            className={({ isActive }) => cn(
              'flex flex-col items-center gap-1 py-2.5 press',
              isActive ? 'text-brand' : 'text-subtle hover:text-text active:text-text',
            )}
          >
            {({ isActive }) => (
              <>
                <Icon size={19} strokeWidth={isActive ? 2.4 : 2} />
                <span className="text-[10px] font-medium leading-none text-center px-1 truncate w-full">
                  {t(labelKey)}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
